/*
Available extended methods (super may be required!)
- refresh                       - runs when drupal is refreshed or can be run internally when required

Available methods
- onReady                       - runs when (min-width: 0rem) is hit
- onMobileNavInit               - runs when (max-width: 46.875rem) is hit
- onMobileNavDestroy            - runs when (max-width: 46.875rem) is hit

Available methods when Config.pluginswindowListenerPlugin.scroll == true
- scroll                        - runs every time scroll events are fired
- debouncedScroll               - runs every configured Xms to delay scroll events and improve performance
- delayedDebouncedScroll        - runs every time scroll events end firing after configured Xms

Available methods when Config.pluginswindowListenerPlugin.resize == true
- resize                        - runs every time resize events are fired
- debouncedResize               - runs every configured Xms to delay resize events and improve performance
- delayedDebouncedResize        - runs every time resize events end firing after configured Xms
*/

// imports
import MainApp from 'minsky-kit/js/managers/MainApp';
import EmbedVideo from 'minsky-kit/js/components/EmbedVideo';
import InstanceManager from 'minsky-kit/js/managers/InstanceManager';
import CTMenuOverlay from './minsky/components/CTMenuOverlay';
import FaqOverview from './minsky/components/FaqOverview';
import Config from './Config'; // keep even though we don't use it
import { prepSvgMask } from './minsky/utils/extraSvgUtils';
import MediaCarousel from './minsky/components/MediaCarousel';
import CircleMenuOverlay from './minsky/components/CircleMenuOverlay';
import AnalystView from './minsky/components/AnalystView';
import InviewManager from './minsky/components/InviewManager';
import RelatedCarousel from './minsky/components/RelatedCarousel';
import HistoryButton from './minsky/components/HistoryButton';
import FormModal from './minsky/components/FormModal';
import DownloadForm from './minsky/components/DownloadForm';

import tippy from 'tippy.js';
import 'tippy.js/animations/shift-away.css';
import AnalystStorage from './minsky/components/AnalystStorage';
import { upperFirst } from 'lodash';

// class
export default class ProjectApp extends MainApp {
// export default class ProjectApp extends MinskyApp {
    // constructor
    constructor (args = {}) {
        // set app instance debug state
        args.debug = true;

        // call super constructor
        super(args, 'Project App');

        // properties
        this.Config = Config;
        this._im = new InstanceManager(); // https://bitbucket.org/Minsky_WebAgency/minsky-kit/src/master/documentation/InstanceManager.md
        this.analystStorage = new AnalystStorage();

        // load mask spritesheet
        this.once('spritesheetsLoaded', () => {
            prepSvgMask(
                'navigation-background'
            );
        })
    }

    // methods
    onReady () {
        this.log('App ready');

        // autofocus searchfield
        const searchField = document.querySelector('#views-exposed-form-search-content-search input');
        if (searchField) {
            searchField.focus();
        }

        // history button for documents
        this._im.initAddComponent('history-button', HistoryButton, '.history-button');

        //add random classes to circle images
        document.querySelectorAll('.circle-image').forEach($ci => {
            const randomInt = getRandomInt(2) + 1;
            $ci.classList.add(`circle-image--${randomInt}`);
        });

        //check for analyst actions
        const $analystActions = document.querySelector('.js-analyst-actions');
        if($analystActions) {
            const $saveBtn = $analystActions.querySelector('.js-analyst-save');
            const $removeBtn = $analystActions.querySelector('.js-analyst-remove');

            if(this.analystStorage.hasAnalyst($analystActions.dataset.id)) {
                $saveBtn.classList.add('noRender');
            } else {
                $removeBtn.classList.add('noRender');
            }

            $saveBtn.addEventListener('click', e => {
                e.preventDefault();
                this.analystStorage.save($analystActions.dataset.id);
                $saveBtn.classList.add('noRender');
                $removeBtn.classList.remove('noRender');
            });

            $removeBtn.addEventListener('click', e => {
                e.preventDefault();
                this.analystStorage.remove($analystActions.dataset.id);
                $removeBtn.classList.add('noRender');
                $saveBtn.classList.remove('noRender');
            });
        }

        //carousels for media
        const $mediaCarousels = document.querySelectorAll('.media-p');
        if ($mediaCarousels.length) {
            $mediaCarousels.forEach(($mc) => {
                const isCarousel = $mc.querySelectorAll('.paragraph--sub').length > 1;
                if(isCarousel) {
                    new MediaCarousel({
                        $el:      $mc.querySelector('.media-p__content'),
                        settings: {
                            slidesPerView:     1,
                        },
                    });
                }
            });
        }

        //menuoverlay
        const $menuToggle = document.querySelector('.js-menu-toggle');
        const $menuContent = document.querySelector('.js-menu-overview');
        if ($menuToggle && $menuContent) {
            const menuOverlay = new CircleMenuOverlay({
                $el: {
                    content:    $menuContent.innerHTML,
                },
                $trigger: $menuToggle,
            });
        }

        //analystView
        const $analystView = document.querySelector('.analyst-view .views-element-container');
        if ($analystView) {
            new AnalystView({
                $el:       $analystView,
                analystStorage: this.analystStorage,
                filters:   [
                    { key: 'type', type: 'checkbox', dropdown: true },
                    { key: 'genre', type: 'checkbox', dropdown: true },
                    { key: 'background', type: 'checkbox', dropdown: true },
                ],
            });
        }

        const $masterStars = document.querySelectorAll('.masterclass-star');
        if($masterStars) {
            $masterStars.forEach($ms => {
                tippy($ms, {
                    content: $ms.dataset.title,
                    theme: `${$ms.dataset.paper ? 'masterclass--paper' : 'masterclass'}`,
                    placement: 'top',
                    animation: 'shift-away',
                    hideOnClick: false,
                })
            });
        }

        const $analystProfile = document.querySelectorAll('.js-analyst-title');
        if($analystProfile) {
            $analystProfile.forEach($ms => {
                tippy($ms, {
                    content: $ms.dataset.title,
                    theme: 'analyst-title',
                    placement: 'top',
                    animation: 'shift-away',
                    hideOnClick: false,
                })
            });
        }

        // video embeds
        this._im.initAddComponent('embed-video', EmbedVideo, '.embed-video');

        // faq overview filter + init accordion in class
        this._im.initAddComponent('faq-overview', FaqOverview, '.faq-overview__overview .views-element-container');

        // media carousel
        this._im.initAddComponent('related-carousel', RelatedCarousel, '.view--articles-latest .view-rows');

        this._im.initAddComponent('form-modal', FormModal, '.js-form-modal');

        this._im.initAddComponent('download-form', DownloadForm, '.js-download-form');
    }

    refresh () {
        this.log('App refresh');

        super.refresh();
    }

    resize () {
        this.log('App resize');
    }

    debouncedResize () {
        this.log('App debounced resize');
    }

    scroll () {
        this.log('App scroll');
    }

    onMobileNavInit () {
        this.log('Mobile');

        // mobile navigation
        const $menu = document.querySelector('.header__navigation');
        const $hamburger = document.querySelector('.hamburger');
        if ($menu && $hamburger) {
            this._im.add('mobile-menu', new CTMenuOverlay({
                $source: $menu,
                $hamburger,
                $parent: document.querySelector('.page__inner'),
            }));
        }
    }

    onMobileNavDestroy () {
        this.log('Desktop');

        // destroy mobile only instances
        this._im.destroy([
            'mobile-menu',
        ]);

        this._im.initAddComponent('inview-manager', InviewManager, '.paragraph--default');
    }
}

function getRandomInt(max) {
    return Math.floor(Math.random() * max);
}

// utilities

// event handlers
